import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPayrollRegister, downloadRegisterCSV, downloadBankFile, getClients } from '../services/api';
import { Download, Building2, AlertCircle, Banknote } from 'lucide-react';
import toast from 'react-hot-toast';

const MONTHS  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const curYear = new Date().getFullYear();
const YEARS   = [curYear - 1, curYear, curYear + 1];
const FMT     = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const FMTN    = (n) => Number(n || 0).toLocaleString('en-IN');

const statusBadge = (s) =>
  s === 'paid'      ? 'bg-green-50 text-green-700' :
  s === 'approved'  ? 'bg-blue-50 text-blue-700'   :
  s === 'processed' ? 'bg-amber-50 text-amber-700'  :
  'bg-gray-50 text-gray-500';

// Scrollable column header helper
const TH = ({ children, right }) => (
  <th className={`whitespace-nowrap text-xs font-medium text-gray-400 px-3 py-3 sticky top-0 bg-gray-50 ${right ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);
const TD = ({ children, bold, green, red, right }) => (
  <td className={`px-3 py-3 text-sm whitespace-nowrap ${bold ? 'font-semibold' : ''} ${green ? 'text-green-700' : red ? 'text-red-600' : 'text-gray-700'} ${right ? 'text-right' : ''}`}>
    {children}
  </td>
);

const useDownload = () => {
  const [loading, setLoading] = useState(false);
  const download = async (fn, params, label) => {
    setLoading(true);
    try {
      const res  = await fn(params);
      const disp = res.headers['content-disposition'] || '';
      const name = disp.match(/filename="(.+)"/)?.[1] || label;
      const url  = URL.createObjectURL(new Blob([res.data]));
      const a    = document.createElement('a'); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${name} downloaded`);
    } catch (e) { toast.error(e.response?.data?.message || 'Download failed'); }
    finally { setLoading(false); }
  };
  return { download, loading };
};

const PayrollRegister = () => {
  const [month,    setMonth]    = useState(new Date().getMonth() + 1);
  const [year,     setYear]     = useState(curYear);
  const [clientId, setClientId] = useState('');
  const [status,   setStatus]   = useState('');
  const { download, loading }   = useDownload();

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-list'],
    queryFn : () => getClients({ limit: 200 }).then(r => r.data.data || [])
  });

  const params = { month, year, ...(clientId ? { client_id: clientId } : {}), ...(status ? { status } : {}) };

  const { data, isLoading } = useQuery({
    queryKey: ['payroll-register', month, year, clientId, status],
    queryFn : () => getPayrollRegister(params).then(r => r.data.data)
  });

  const rows   = data?.rows   || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Payroll Register</h1>
          <p className="text-sm text-gray-400 mt-0.5">Full salary breakdown for all employees</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => download(downloadBankFile, { ...params, format: 'neft' }, `Bank_Payment_${MONTHS[month-1]}_${year}.csv`)}
            disabled={loading}
            className="flex items-center gap-2 border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
            <Banknote size={15} />
            Bank File
          </button>
          <button
            onClick={() => download(downloadRegisterCSV, params, `Payroll_Register_${MONTHS[month-1]}_${year}.csv`)}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            <Download size={15} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select value={month} onChange={e => setMonth(+e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(+e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={clientId} onChange={e => setClientId(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          {['processed','approved','paid'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {rows.length > 0 && (
          <span className="ml-auto flex items-center text-sm text-gray-400">
            {rows.length} employee{rows.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Summary tiles */}
      {rows.length > 0 && (
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: 'Gross Salary',      v: totals.gross_salary,    c: 'text-gray-900'    },
            { label: 'PF (Employee)',      v: totals.pf_employee,     c: 'text-red-600'     },
            { label: 'ESI (Employee)',     v: totals.esic_employee,   c: 'text-orange-600'  },
            { label: 'Net Salary',         v: totals.net_salary,      c: 'text-green-700'   },
            { label: 'Total CTC',          v: totals.ctc,             c: 'text-blue-600'    }
          ].map(({ label, v, c }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-400 mb-0.5">{label}</p>
              <p className={`text-base font-bold ${c}`}>₹{FMTN(Math.round(v || 0))}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-auto" style={{ maxHeight: '60vh' }}>
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading register...</div>
        ) : !rows.length ? (
          <div className="text-center py-16">
            <AlertCircle size={28} className="mx-auto text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">No payroll records for {MONTHS[month-1]} {year}.</p>
            <p className="text-xs text-gray-300 mt-1">Process payroll first from the Payroll page.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100">
              <tr>
                <TH>Employee</TH>
                <TH>Client</TH>
                <TH>Paid / Work</TH>
                {/* Earnings */}
                <TH right>Basic</TH>
                <TH right>HRA</TH>
                <TH right>DA</TH>
                <TH right>Conv.</TH>
                <TH right>Medical</TH>
                <TH right>Other</TH>
                <TH right>OT</TH>
                <TH right>Bonus</TH>
                <TH right>Gross</TH>
                {/* Deductions */}
                <TH right>PF Emp</TH>
                <TH right>PF Emplr</TH>
                <TH right>ESI Emp</TH>
                <TH right>ESI Emplr</TH>
                <TH right>PT</TH>
                <TH right>TDS</TH>
                <TH right>Other Ded</TH>
                <TH right>Advance</TH>
                <TH right>Net Salary</TH>
                <TH>Status</TH>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-3 min-w-40">
                    <p className="font-medium text-gray-900 text-sm">{r.employee_name}</p>
                    <p className="text-xs text-gray-400">{r.employee_code} · {r.designation}</p>
                  </td>
                  <td className="px-3 py-3 min-w-32 text-xs text-gray-600">{r.client}</td>
                  <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                    {Number(r.paid_days).toFixed(1)} / {r.working_days}
                  </td>
                  <TD right>{FMT(r.basic)}</TD>
                  <TD right>{FMT(r.hra)}</TD>
                  <TD right>{FMT(r.da)}</TD>
                  <TD right>{FMT(r.conveyance)}</TD>
                  <TD right>{FMT(r.medical)}</TD>
                  <TD right>{FMT(r.other_allowances)}</TD>
                  <TD right>{FMT(r.overtime_amount)}</TD>
                  <TD right>{FMT(r.bonus)}</TD>
                  <TD right bold>{FMT(r.gross_salary)}</TD>
                  <TD right red>{FMT(r.pf_employee)}</TD>
                  <TD right>{FMT(r.pf_employer)}</TD>
                  <TD right red>{FMT(r.esic_employee)}</TD>
                  <TD right>{FMT(r.esic_employer)}</TD>
                  <TD right red>{FMT(r.professional_tax)}</TD>
                  <TD right red>{FMT(r.tds)}</TD>
                  <TD right red>{FMT(r.other_deductions)}</TD>
                  <TD right red>{FMT(r.advance_deduction)}</TD>
                  <TD right bold green>{FMT(r.net_salary)}</TD>
                  <td className="px-3 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(r.status)}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Totals row */}
            <tfoot className="border-t-2 border-gray-300 bg-gray-50 sticky bottom-0">
              <tr>
                <td className="px-3 py-3 font-semibold text-gray-700 text-sm">Total ({rows.length})</td>
                <td colSpan={2} />
                {[totals.basic, totals.hra, totals.da, totals.conveyance, totals.medical,
                  totals.other_allowances, totals.overtime_amount, totals.bonus, totals.gross_salary
                ].map((v, i) => (
                  <td key={i} className={`px-3 py-3 text-right text-sm font-semibold ${i === 8 ? 'text-gray-900' : 'text-gray-700'}`}>
                    {FMT(v)}
                  </td>
                ))}
                {[totals.pf_employee, totals.pf_employer, totals.esic_employee, totals.esic_employer,
                  totals.professional_tax, totals.tds, totals.other_deductions, totals.advance_deduction
                ].map((v, i) => (
                  <td key={i} className="px-3 py-3 text-right text-sm font-semibold text-red-600">{FMT(v)}</td>
                ))}
                <td className="px-3 py-3 text-right text-sm font-bold text-green-700">{FMT(totals.net_salary)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Bank file note */}
      {rows.length > 0 && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center gap-3">
          <Banknote size={16} className="text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            <strong>Bank File</strong> generates a NEFT/ECS-compatible CSV with account numbers and IFSC codes for approved payrolls. Employees without bank details on file will be excluded.
          </p>
        </div>
      )}
    </div>
  );
};

export default PayrollRegister;
